import { Component, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import moment from 'moment';
import { BaseComponent } from 'src/app/common/base/base.component';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { CancelDialogComponent } from 'src/app/controls/cancel-dialog/cancel-dialog.component';
import { FormService } from 'src/app/controls/form-table/form.service';
import { OtherSystem } from 'src/app/controls/other-system-chips/other-system';
import { utils } from 'src/app/modules/libs/utils';
import { Form, FormHandler } from 'src/app/services/forms/form';
import { FormRestriction } from 'src/app/services/forms/form-restriction';
import { FormVersion, FormVersionHandler } from 'src/app/services/forms/form-version';
import { FormVersionService } from 'src/app/services/forms/form-version.service';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { WfSignatureResource } from 'src/app/components/workflow/workflow';
import { PPSTBShutter } from '../../ppstb-shutter';
import { PPSTBAcceleratorLocation } from '../../ppstb-accelerator-location';
import { PpstbService } from '../../ppstb.service';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { FormType } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'ppstb-main-s1',
  templateUrl: './ppstb-main-s1.component.html',
  styleUrls: ['./ppstb-main-s1.component.scss']
})
export class PpstbMainS1Component extends BaseComponent implements OnChanges {

  @Input() form?: Form | null;
  @Input() disabled: boolean = true;
  @Input() showAmendment?: boolean;
  @Input() isLogView?: boolean;

  @Output() formCreated = new EventEmitter<Form>();
  @Output() formCancelled = new EventEmitter<Form | null | undefined>();

  requesterCtrl = new FormControl({}, Validators.required);
  descriptionCtrl = new FormControl('', Validators.required);
  shuttersCtrl = new FormControl();
  acceleratorCtrl = new FormControl();
  chkTopOffCtrl = new FormControl();
  chkRadMonCtrl = new FormControl();
  otherSystemCtrl = new FormControl();
  chkBeamlineCtrl = new FormControl();
  chkAcceleratorCtrl = new FormControl();
  startDateCtrl = new FormControl();
  completionDateCtrl = new FormControl();
  restrictionsCtrl = new FormControl();
  bypassCtrl = new FormControl('', Validators.required);
  conditionsCtrl = new FormControl('', Validators.required);
  systemCtrl = new FormControl('', Validators.required);
  testingCtrl = new FormControl();

  formID?: number;
  locationIDs?: (number | undefined)[];

  // tmpChkBeamline?: boolean;
  // tmpChkAccelerator?: boolean;
  // tmpChkTopOff?: boolean;
  // tmpRadMonOff?: boolean;
  otherSystems?: OtherSystem[];
  // tmpStartDate!: Date;
  // tmpCompletionDate!: Date;

  signature?: WfSignatureResource;
  amendmentSignature?: WfSignatureResource;
  canUnsubmit!: boolean;
  activeUsers?: User[];
  technicalApprovalSignatureAmendment?: WfSignatureResource;
  formVersion?: FormVersion;

  constructor(
    protected override injector: Injector,
    public service: PpstbService,
    private formService: FormService,
    private formVersionService: FormVersionService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
    this.activeUsers = this.users.filter(x => x.status == 1);
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      requesterCtrl: this.requesterCtrl,
      descriptionCtrl: this.descriptionCtrl,
      shuttersCtrl: this.shuttersCtrl,
      acceleratorCtrl: this.acceleratorCtrl,
      chkBeamlineCtrl: this.chkBeamlineCtrl,
      chkAcceleratorCtrl: this.chkAcceleratorCtrl,
      chkTopOffCtrl: this.chkTopOffCtrl,
      chkRadMonCtrl: this.chkRadMonCtrl,
      otherSystemCtrl: this.otherSystemCtrl,
      startDateCtrl: this.startDateCtrl,
      completionDateCtrl: this.completionDateCtrl,
      restrictionsCtrl: this.restrictionsCtrl,
      bypassCtrl: this.bypassCtrl,
      testingCtrl: this.testingCtrl,
      conditionsCtrl: this.conditionsCtrl,
      systemCtrl: this.systemCtrl
    });
    this.formGroup.markAsPristine();

    this.formGroup.valueChanges.subscribe(() => {
      if (this.formGroup.dirty)
        this.setFormDirty();
    });

    this.checkEnableControls();
  }

  /**
   * Enables PPSTB editing only for the current editor and never inside history snapshots.
   */
  checkEnableControls() {
    this.disabled = true;
    this.formGroup?.disable();

    if (this.form) {
      let enable: boolean | null = false;
      if (!this.showAmendment)
        enable = this.form?.editingById == this.getCurrentUser()?.id && this.hasPrivilege(this.privilegeEnum.PPSTBNewForm) && ((this.form?.formVersion.statusID ?? 0) == this.formStatusEnum.New || this.form?.formVersion?.statusID == this.formStatusEnum.Draft);
      else
        enable = this.form?.editingById == this.getCurrentUser()?.id && this.hasPrivilege(this.privilegeEnum.PPSTBNewForm) && (this.form?.formVersion?.statusID == this.formStatusEnum.AmendmentDrafting && this.hasPrivilege(this.privilegeEnum.PPSTBEditAmendment));

      if (enable && !this.isLogView) {
        this.disabled = false;
        this.formGroup?.enable();
      }
    }
  }

  refresh() {
    if (this.form) {
      if (this.showAmendment) {
        this.formVersion = utils.cloneDeep(this.form.formVersion);
      }
      else {
        // While an amendment is pending, the regular view remains on the original
        // version; once it is activated, the amended active version is the form to display.
        if (this.form.formVersion.status?.amendment && this.form.formVersion.previousVersion)
          this.formVersion = utils.cloneDeep(this.form.formVersion.previousVersion);
        else
          this.formVersion = utils.cloneDeep(this.form.formVersion);
      }
      this.descriptionCtrl.setValue(this.formVersion.ppstb?.justificationForBypass ?? '');
      this.bypassCtrl.setValue(this.formVersion.ppstb?.bypassDetails ?? '');
      this.conditionsCtrl.setValue(this.formVersion.ppstb?.conditionsAndLimitations ?? '');
      this.systemCtrl.setValue(this.formVersion.ppstb?.systemAndPrintNumbers ?? '');
      this.requesterCtrl.setValue(this.formVersion.requester);
      this.chkBeamlineCtrl.setValue(this.formVersion.ppstb?.beamline);
      this.chkAcceleratorCtrl.setValue(this.formVersion.ppstb?.accelerator);
      this.chkTopOffCtrl.setValue(this.formVersion.ppstb?.topOff);
      this.chkRadMonCtrl.setValue(this.formVersion.ppstb?.radiationMonitoringSystem);
      this.otherSystems = this.formVersion.ppstb?.ppstbOtherSystems.map(o => {
        const os: OtherSystem = { id: o.id, text: o.text };
        return os;
      });
      this.otherSystemCtrl.setValue(this.otherSystems);
      this.startDateCtrl.setValue(this.formVersion.ppstb?.permittedStartDate);
      this.completionDateCtrl.setValue(this.formVersion.ppstb?.estimatedCompletion);
      this.getLocationIDs();
      this.signature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 1)?.wfSignatures?.[0];
      this.amendmentSignature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 4)?.wfSignatures?.find(s => s.amendmentEnabled);
      this.canUnsubmit = !this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 2)?.wfSignatures?.some(s => s.signed);
      this.technicalApprovalSignatureAmendment = this.form.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 12)?.wfSignatures?.find(s => s.code == 'TECHNICAL-APPROVAL');
    }
    else {
      this.clearFields();
    }
    this.formGroup?.disable();
    this.formGroup?.markAsPristine();
    this.checkEnableControls();
  }

  clearFields() {
    this.formGroup?.reset();
    // this.tmpChkBeamline = false;
    // this.tmpChkAccelerator = false;
    // this.tmpChkTopOff = false;
    // this.tmpRadMonOff = false;
    // this.tmpOtherSystems = [];
  }

  selectedReq(e: MatAutocompleteSelectedEvent) {
    const value = e.option.value;
    if (this.formVersion) {
      this.formVersion.requesterID = value.id;
      this.setFormDirty();
    }
  }

  onReqChange(e: any) {
    const text = e.target.value as string;
    this.activeUsers = this.users.filter(x => x.status == 1 && x.name?.toLowerCase().includes(text.toLowerCase()));
  }

  descriptionChanged(e: any) {
    if (this.formVersion?.ppstb) {
      this.formVersion.ppstb.justificationForBypass = e;
      this.descriptionCtrl.setValue(e);
      this.setFormDirty();
    }
  }

  relatedDocumentsChanged(e: any) {
    if (this.formVersion?.ppstb) {
      this.formVersion.ppstb.relatedDocuments = e;
      this.setFormDirty();
    }
  }

  onShutterEdited(e: Resource[]) {
    e.map(r => {
      if (!this.formVersion?.ppstb?.ppstbShutters?.find(s => s.resourceID == r.id))
        this.formVersion?.ppstb?.ppstbShutters?.push({ id: 0, ppstbID: this.formVersion.ppstb?.id, resource: r, resourceID: r.id });
    });
    this.formVersion?.ppstb?.ppstbShutters?.map((s, i) => {
      if (!e.find(r => r.id == s.resourceID))
        this.formVersion?.ppstb?.ppstbShutters?.splice(i, 1);
    });
    this.setFormDirty();
    this.getLocationIDs();
  }

  onAcceleratorEdited(e: Resource[]) {
    e.map(r => {
      if (!this.formVersion?.ppstb?.ppstbAcceleratorLocations?.find(s => s.resourceID == r.id))
        this.formVersion?.ppstb?.ppstbAcceleratorLocations?.push({ id: 0, ppstbID: this.formVersion?.ppstb?.id, resource: r, resourceID: r.id });
    });
    this.formVersion?.ppstb?.ppstbAcceleratorLocations?.map((s, i) => {
      if (!e.find(r => r.id == s.resourceID))
        this.formVersion?.ppstb?.ppstbAcceleratorLocations?.splice(i, 1);
    });
    this.setFormDirty();
    this.getLocationIDs();
  }

  onOtherSystemsEdited(e: OtherSystem[]) {
    e.map(o => {
      if (!this.formVersion?.ppstb?.ppstbOtherSystems?.find(x => x.text == o.text))
        this.formVersion?.ppstb?.ppstbOtherSystems.push({ id: 0, text: o.text, ppstbID: this.formVersion?.ppstb.id });
    });
    this.formVersion?.ppstb?.ppstbOtherSystems.map((x, i) => {
      if (!e.find(o => o.text == x.text))
        this.formVersion?.ppstb?.ppstbOtherSystems.splice(i, 1);
    });
    this.setFormDirty();
  }

  blCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.ppstb) {
      this.setFormDirty();
      this.formVersion.ppstb.beamline = checked;
      if (!checked)
        this.formVersion.ppstb.ppstbShutters = [];
    }
    this.getLocationIDs();
  }

  accCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.ppstb) {
      this.setFormDirty();
      this.formVersion.ppstb.accelerator = checked;
      if (!checked)
        this.formVersion.ppstb.ppstbAcceleratorLocations = [];
    }
    this.getLocationIDs();
  }

  topCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.ppstb) {
      this.setFormDirty();
      this.formVersion.ppstb.topOff = checked;
    }
    this.getLocationIDs();
  }

  radCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.ppstb) {
      this.setFormDirty();
      this.formVersion.ppstb.radiationMonitoringSystem = checked;
    }
    this.getLocationIDs();
  }

  getLocationIDs() {
    this.locationIDs = this.formVersion?.ppstb?.ppstbShutters.map(s => s.resourceID).concat(this.formVersion.ppstb.ppstbAcceleratorLocations.map(l => l.resourceID ?? 0));
  }

  /**
   * Returns only populated resource navigation objects from PPSTB location relations.
   * @param e PPSTB shutter or accelerator-location relations.
   */
  getResources(e: PPSTBShutter[] | PPSTBAcceleratorLocation[] | undefined): Resource[] {
    // Custom restrictions and update responses may contain relations whose resource navigation is not populated.
    return e?.map(x => x.resource).filter((resource): resource is Resource => !!resource) ?? [];
  }

  restrictionsChanged(e: FormRestriction[]) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.ppstbRestrictions = e;
    this.setFormDirty();
  }

  bypassDetailsChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.bypassDetails = e;
    this.bypassCtrl.setValue(e);
    this.setFormDirty();
  }

  testingRequiredChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.testingRequiredBeforeOperation = e;
    this.bypassCtrl.setValue(e);
    this.setFormDirty();
  }

  conditionsAndlimitationsChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.conditionsAndLimitations = e;
    this.conditionsCtrl.setValue(e);
    this.setFormDirty();
  }

  systemAndPrintChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.systemAndPrintNumbers = e;
    this.systemCtrl.setValue(e);
    this.setFormDirty();
  }

  startDateChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.permittedStartDate = e.value;
    this.setFormDirty();
  }

  completionDateChanged(e: any) {
    if (this.formVersion?.ppstb)
      this.formVersion.ppstb.estimatedCompletion = e.value;
    this.setFormDirty();
  }

  async save(keepLoading: boolean = false) {
    return new Promise<boolean>(async (resolve, reject) => {
      if (this.form && this.formVersion && !this.form?.id) {
        this.form.formVersion = this.formVersion;
        this.formService.create(FormHandler.toSave(this.form))?.subscribe((data: Form) => {
          this.form = data;
          this.setFormDirty(false);
          this.setOpenDocument(data.type, data.id);
          this.formCreated.emit(data);
          this.refresh();
          this.alert.message('DraftSaved', [
            new MessagePlaceholder("{serialNo}", data.sequenceNumber.number)]);

          resolve(true);
        },
          (error) => {
            console.error(error);
            this.alert.defaultError();
            reject(false);
          }
        );
      } else if (this.formVersion) {
        this.formVersionService.update(FormVersionHandler.toSave(this.formVersion, FormType.PPSTB)).subscribe(() => {
          this.setFormDirty(false);
          this.showAmendment ? this.alert.message('amendment saved', [
            new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber.number)]) : this.alert.message('DraftUpdated', [
              new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber.number)]);
          resolve(true);
        },
          (error) => {
            console.error(error);
            this.alert.defaultError();
            reject(false);
          }
        );
      }
    });
  }

  async submit() {
    if (this.validateSubmit() && this.form) {
      if (await this.save()) {
        if (this.signature) {
          this.service.sign(this.form, this.signature.taskID, this.signature.approveID).then(data => {
          });
        }
      }
    }
  }

  unsubmit() {
    if (this.signature)
      this.service.sign(this.form, this.signature.taskID, this.signature.unapproveID);
  }

  disapprove() {
    const signature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 2)?.wfSignatures?.find(s => s.disapproveID);
    if (signature)
      this.service.sign(this.form, signature.taskID, signature.disapproveID);
  }

  validateSubmit(): boolean {
    let result = true;
    if (!this.formVersion?.requesterID) {
      this.alert.message('PPSTB_Requester_Validation');
      this.requesterCtrl.setErrors({ required: true });
      result = false;
    }

    if (
      !this.formVersion?.ppstb?.justificationForBypass ||
      this.formVersion.ppstb.justificationForBypass.trim() === ""
    ) {
      this.alert.message('PPSTB_Justification_Validation');
      this.descriptionCtrl.setErrors({ required: true });
      result = false;
    }

    if (this.formVersion?.ppstb?.beamline && !this.formVersion.ppstb.ppstbShutters.length) {
      this.shuttersCtrl.setErrors({ required: true });
      this.alert.message('EB_Please_Check_On_At_Least_One_System_Affected');
      result = false;
    }

    if (this.formVersion?.ppstb?.accelerator && !this.formVersion.ppstb.ppstbAcceleratorLocations) {
      this.acceleratorCtrl.setErrors({ required: true });
      this.alert.message('EB_Please_Check_On_At_Least_One_System_Affected');
      result = false;
    }

    if (!this.formVersion?.ppstb?.ppstbOtherSystems.length &&
      !this.formVersion?.ppstb?.ppstbShutters.length &&
      !this.formVersion?.ppstb?.ppstbAcceleratorLocations.length
    ) {
      this.alert.message('PPSTB_Please_Check_At_Least_One_System_Affected');
      this.otherSystemCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.ppstbRestrictions.length) {
      this.alert.message('PPSTB_No_Mitigations_Were_Added');
      this.restrictionsCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.bypassDetails || this.formVersion.ppstb.bypassDetails.trim() === "") {
      this.alert.message('PPSTB_Bypass_Details_Validation');
      this.bypassCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.conditionsAndLimitations || this.formVersion.ppstb.conditionsAndLimitations.trim() === "") {
      this.alert.message('PPSTB_Conditions_And_Limitations_Validations');
      this.conditionsCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.systemAndPrintNumbers || this.formVersion.ppstb.systemAndPrintNumbers.trim() === "") {
      this.alert.message('EB_No_System_And_Print_Numbers_Were_Specified');
      this.systemCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.permittedStartDate) {
      this.alert.message('PPSTB_No_Permitted_Start_Date_Was_Specified');
      this.startDateCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.ppstb?.estimatedCompletion) {
      this.alert.message('PPSTB_Estimated_Completion_Validation');
      this.completionDateCtrl.setErrors({ required: true });
      result = false;
    }

    if (this.formVersion?.ppstb?.estimatedCompletion && this.formVersion.ppstb.permittedStartDate) {
      if (moment(this.formVersion.ppstb.estimatedCompletion).isBefore(this.formVersion.ppstb.permittedStartDate)
      ) {
        this.alert.message('PPSTB_Estimated_Completion_Less_Than_Permitted_Start_Date');
        this.completionDateCtrl.setErrors({ invalidDate: true });
        result = false;
      }
    }

    if (!result)
      this.formGroup?.markAllAsTouched();

    return result;
  }

  /**
   * Saves the amendment and advances its initiation workflow using the pending submit action.
   */
  submitAmendment() {
    if (this.validateSubmit() && this.form && this.formVersion) {
      this.formVersionService.update(FormVersionHandler.toSave(this.formVersion, FormType.PPSTB)).subscribe(async (data: FormVersion) => {
        if (this.form && data) {
          this.formVersion = data;
          // Use the Global Workflow identifier propagated through WFTaskConfigID instead of section or title assumptions.
          const signature = this.form.wfTable?.wfTableLocal?.wfSectionLocals
            .flatMap(s => s.wfSignatures ?? [])
            .find(s => s.code == 'PPSTB-AMENDMENT-SUBMIT' && !s.signed && !!s.approveID);
          if (signature)
            await this.service.sign(this.form, signature.taskID, signature.approveID).then(() => {
              this.setFormDirty(false);
            });
          else
            this.alert.defaultError();
        }
      },
        (error) => {
          console.error(error);
          this.alert.defaultError();
        }
      );
    }
  }

  /**
   * Returns a submitted PPSTB amendment to drafting through its coded workflow task.
   */
  unsubmitAmendment() {
    // Resolve the same Global Workflow task used for submission without assuming its local section.
    const signature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(s => s.wfSignatures ?? [])
      .find(s => s.code == 'PPSTB-AMENDMENT-SUBMIT' && s.signed && s.unapproveEnabled && !!s.unapproveID);
    if (signature)
      this.service.sign(this.form, signature.taskID, signature.unapproveID);
    else
      this.alert.defaultError();
  }

  disapproveAmendment() {
    const signature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 12)?.wfSignatures?.find(s => s.disapproveID);
    if (signature) {
      this.service.sign(this.form, signature.taskID, signature.disapproveID);
    }
  }

  // cancelNew() {
  //   const amendment = this.formVersion?.status.amendment;
  //   const confirm = this.dialog.open(YesNoDialogComponent, {
  //     width: "600px",
  //     data: {
  //       message: this.getMessage('CancelFormDialogTitle')?.description
  //     },
  //   });

  //   confirm.afterClosed().subscribe(async (data) => {
  //     if (data) {
  //       if (amendment) {
  //         // this.alert.message(this.message_amendment_serial_no_deleted, [
  //         //   new Placeholder("{serialNo}", this.form_?.sequenceNumber?.number),
  //         // ]);
  //       } else {
  //         if (this.form?.sequenceNumber.number) {
  //           await this.formService.delete(this.form.sequenceNumber.number).subscribe(data => {
  //             this.alert.message('Form_SerialNo_deleted', [
  //               new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber?.number),
  //             ]);
  //             this.form = null;
  //             this.refresh();
  //             this.formCancelled.emit();
  //             this.setFormDirty(false);
  //           });
  //         }
  //       }
  //     }
  //   });
  // }

  /**
   * Cancels the current PPSTB or amendment through the applicable workflow action.
   * The canceled form is emitted so the parent can restore the active version after an amendment cancellation.
   */
  async cancel() {
    if (this.form?.formVersion?.statusID == this.formStatusEnum.New) {
      try {
        await this.save();
      } catch (err) {
        console.log("Error saving form before canceling:", err);
        return;
      }
    }
    const amendment = this.form?.formVersion.status.amendment;
    const confirm = this.dialog.open(CancelDialogComponent, {
      width: "600px",
      data: {
        amendment: this.form?.formVersion.status.amendment,
      },
    });

    const amendmentSignatures = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(section => section.wfSignatures ?? []);
    // Resolve the amendment cancellation action by task code first, then support legacy workflow layouts.
    const signature = amendment
      ? amendmentSignatures?.find(s => s.code == 'PPSTB-AMENDMENT-SUBMIT' && s.cancelEnabled && !!s.cancelID)
        ?? amendmentSignatures?.find(s => s.cancelEnabled && !!s.cancelID)
      : this.signature;
    confirm.afterClosed().subscribe(data => {
      if (data?.accept) {
        if (!signature?.taskID || !signature.cancelID) {
          this.alert.defaultError();
          return;
        }
        const cancelledForm = this.form;
        this.service.sign(this.form, signature.taskID, signature.cancelID, data.text).then(result => {
          if (!result) return;
          if (!amendment) {
            this.alert.message('CancelSuccess', [new MessagePlaceholder("{reason}", data.text),]);
          }
          this.form = null;
          this.setFormDirty(false);
          this.refresh();
          this.formCancelled.emit(cancelledForm);
        }).catch(() => { this.alert.defaultError(); });
      }
    });
  }


  diffRelatedDocuments(): boolean | undefined {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.relatedDocuments?.slice().sort((a, b) => utils.sort(a.relatedDocumentId, b.relatedDocumentId, true)).map((l) => {
        return {
          id: l.relatedDocumentId
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.relatedDocuments?.slice().sort((a, b) => utils.sort(a.relatedDocumentId, b.relatedDocumentId, true)).map((l) => {
      return {
        id: l.relatedDocumentId
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffRelatedLinks() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.ppstbRelatedLinks?.slice().sort((a, b) => utils.sort(a.id, b.id, true)).map((l) => {
        return {
          id: l.id,
          text: l.text,
          url: l.url
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.ppstbRelatedLinks?.slice().sort((a, b) => utils.sort(a.id, b.id, true)).map((l) => {
      return {
        id: l.id,
        text: l.text,
        url: l.url
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffRestrictions() {
    if (!this.showAmendment) return;
    // Sort copies only: these live arrays drive the restriction table's display
    // order, and an in-place sort here reshuffles the visible list.
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.ppstbRestrictions?.slice().sort((a, b) => utils.sort(a.restrictionID, b.restrictionID, true)).map((l) => {
        return {
          restrictionID: l.restrictionID,
          text: l.text,
        } as FormRestriction;
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.ppstbRestrictions?.slice().sort((a, b) => utils.sort(a.restrictionID, b.restrictionID, true)).map((l) => {
      return {
        restrictionID: l.restrictionID,
        text: l.text,
      } as FormRestriction;
    }))?.replace(/\s/g, "");
    this.formVersion?.ppstb?.ppstbRestrictions.map(r => {
      r.hilite = this.formVersion?.previousVersion?.ppstb?.ppstbRestrictions.find(p => p.restrictionID ? p.restrictionID == r.restrictionID : p.text == r.text) == null;
    });
    return current != original;
  }

  diffShutters() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.ppstbShutters?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
        return {
          id: l.resourceID
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.ppstbShutters?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
      return {
        id: l.resourceID
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffAccelerators() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.ppstbAcceleratorLocations?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
        return {
          id: l.resourceID
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.ppstbAcceleratorLocations?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
      return {
        id: l.resourceID
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffOtherSystems() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.ppstb?.ppstbOtherSystems?.slice().sort((a, b) => utils.sort(a.text, b.text, true)).map((l) => {
        return {
          text: l.text,
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.ppstb?.ppstbOtherSystems?.slice().sort((a, b) => utils.sort(a.text, b.text, true)).map((l) => {
      return {
        text: l.text,
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }


}
